// Library imports
import React, { useEffect } from "react";
import { RootState } from "store";
import { useSelector } from "react-redux";
import styled from "styled-components";

// Local imports
import UserListItem from "./UserListItem";
import Checkbox from "../../../Common/Components/Checkbox";
import PeopleListHolder from "./PeopleListHolder";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";
import DownAngle from "../../../../assets/img/icons/DownAngle.svg";
import { IContactSorting } from "../interface";
import { resetAudiencePeopleTabData } from "../Lists/actions";
import { getAudienceAllPeopleList } from "../actions";
import { useHistory } from "react-router-dom";
import { getQueryParam } from "utils";

interface UserListProps {
  sort?: boolean;
  showDetailProfileHandler?(): void;
  list?: any;
  loadingState?: boolean;
  page?: number;
  onCheckHandler?: (values: any) => void;
  clearSelection?: boolean;
  handleSelectAll?: any;
  selectAll?: any;
  setSelectAll?: any;
  checkedItems?: any;
  setCheckedItems?: any;
  tabState?: string;
  deleteAllFlag?: boolean;
  setDeleteAllFlag?: any;
  deleteAllTotalCount?: number;
  contactCheckHandler?: (id: string, isChecked: boolean) => void;
  handleReselectItem?: (id: string) => void;
  allPeoplePage?: number;
  selectedPersonalityType?: any;
  profiledPeoplePage?: number;
  unprofiledPeoplePage?: number;
  checkedItemsPeopleTab?: any;
  setCheckedItemsPeopleTab?: (value: any) => void;
  clearSelectionHandler: () => void;
  sortObj: IContactSorting;
  setSortObj: (value: IContactSorting) => void;
  setWorkflowEmailId: (value: string) => void;
  setWorkflowUuid: (value: string) => void;
}

const UserList: React.FC<UserListProps> = (props) => {
  // Local States
  const {
    list,
    onCheckHandler,
    clearSelection,
    handleSelectAll,
    checkedItems,
    setCheckedItems,
    selectAll,
    setSelectAll,
    tabState,
    deleteAllFlag,
    setDeleteAllFlag,
    contactCheckHandler,
    handleReselectItem,
    checkedItemsPeopleTab,
    setCheckedItemsPeopleTab,
    showDetailProfileHandler,
    clearSelectionHandler,
    sortObj,
    setSortObj,
    setWorkflowEmailId,
    setWorkflowUuid,
  } = props;
  const history = useHistory();

  // Redux States
  const allPeopleListLoading: boolean = useSelector((state: RootState) => state.peopleAudience.allPeopleloading);
  const allPeopleList: any = useSelector((state: RootState) => state.peopleAudience.allPeople);
  const searchTerm = useSelector((state: RootState) => state.peopleAudience.searchTermPeople);
  const audienceFiltersApplied: boolean = useSelector(
    (state: RootState) => state.uiReducer.audienceFiltersApplied
  );

  useEffect(() => {
    //  for clearing selection when clicked from delete bottom bar
    if (clearSelection && checkedItems.length > 0) {
      setCheckedItems([]);
    }
  }, [clearSelection]);

  const onClickChecked = (id: any, name: string) => {
    if (checkedItems.includes(id)) {
      let arr = [...checkedItems].filter((item) => item !== id);
      let arr2 = checkedItemsPeopleTab.filter((item: any) => item.id !== id);
      if (selectAll) setSelectAll(false);
      //! adding this to remove footer
      if (arr.length === 0 && deleteAllFlag) setDeleteAllFlag(false);
      setCheckedItems([...arr]);
      setCheckedItemsPeopleTab && setCheckedItemsPeopleTab(arr2);
      if (onCheckHandler) onCheckHandler(arr2);
    } else {
      setCheckedItems([...checkedItems, id]);
      setCheckedItemsPeopleTab && setCheckedItemsPeopleTab([...checkedItemsPeopleTab, { id, name }]);
      if (onCheckHandler) onCheckHandler([...checkedItemsPeopleTab, { id, name }]);
      if (!selectAll && [...checkedItems, id].length === list.length) setSelectAll(true);
    }
  };

  const sortHandler = (sortingColumn: string) => {
    clearSelectionHandler(); // clear any selected contacts
    resetAudiencePeopleTabData(); // reset contacts
    if (sortingColumn === "engagement_score") {
      if (sortObj.sortType === "ASC") {
        setSortObj({ sortBy: sortingColumn, sortType: "DESC" }); // sort by ascending
      } else {
        setSortObj({ sortBy: sortingColumn, sortType: "ASC" }); // sort by descending
      }
    } else {
      if (sortObj.sortBy === sortingColumn) {
        if (sortObj.sortType === "") {
          setSortObj({ sortBy: sortingColumn, sortType: "ASC" }); // sort by ascending
        } else if (sortObj.sortType === "ASC") {
          setSortObj({ sortBy: sortingColumn, sortType: "DESC" }); // sort by descending
        } else {
          setSortObj({ sortBy: "engagement_score", sortType: "DESC" }); // reset  sorting to default
        }
      } else {
        setSortObj({ sortBy: sortingColumn, sortType: "ASC" });
      }
    }
  };

  const viewAllContactsClickHandler = () => {
    history.push("/audiences/people");
    setWorkflowEmailId("");
    setWorkflowUuid("");
    getAudienceAllPeopleList(20, 0, "all", "", [], [], [], [], "engagement_score", "DESC", "", "");
  };

  return (
    <Wrapper
      allPeopleListLoading={allPeopleListLoading}
      id="scrollableDiv"
      className={`${props.sort ? "sortable" : ""} ${checkedItems.length > 0 ? "set-table-height-people" : ""}`}
    >
      <table>
        <thead hidden={allPeopleList.length === 0 && !allPeopleListLoading}>
          <tr>
            <td className="checkbox">
              <Checkbox
                onClick={() => setDeleteAllFlag && setDeleteAllFlag(!selectAll)}
                checked={selectAll}
                onChange={handleSelectAll}
              />
            </td>
            <td className="h-name">
              <span className="sortable-td" onClick={() => sortHandler("full_name")}>
                <span className="left-align">Name</span>
                <span
                  className={`upArrow arrow-opacity ${
                    sortObj.sortBy === "full_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
                <span
                  className={`downArrow arrow-opacity ${
                    sortObj.sortBy === "full_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
              </span>
            </td>
            <td className="company-col">
              <span className="sortable-td" onClick={() => sortHandler("company_name")}>
                <span className="left-align">Company</span>
                <span
                  className={`upArrow arrow-opacity ${
                    sortObj.sortBy === "company_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
                <span
                  className={`downArrow arrow-opacity ${
                    sortObj.sortBy === "company_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
              </span>
            </td>
            <td className="personality-col">
              <span className="sortable-td" onClick={() => sortHandler("personality_name")}>
                <span className="left-align">Personality</span>
                <span
                  className={`upArrow arrow-opacity ${
                    sortObj.sortBy === "personality_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
                <span
                  className={`downArrow arrow-opacity ${
                    sortObj.sortBy === "personality_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
              </span>
            </td>
            <td className="status-col">
              <span className="sortable-td" onClick={() => sortHandler("status")}>
                <span className="left-align">Status</span>
                <span
                  className={`upArrow arrow-opacity ${
                    sortObj.sortBy === "status" && sortObj.sortType === "DESC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
                <span
                  className={`downArrow arrow-opacity ${
                    sortObj.sortBy === "status" && sortObj.sortType === "ASC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
              </span>
            </td>
            <td className="last-item">
              <span className="sortable-td" onClick={() => sortHandler("engagement_score")}>
                <span className="engagement-float">Engagement Score</span>
                <span
                  className={`upArrow engagement-arrow arrow-opacity ${
                    sortObj.sortBy === "engagement_score" && sortObj.sortType === "DESC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
                <span
                  className={`downArrow engagement-arrow arrow-opacity ${
                    sortObj.sortBy === "engagement_score" && sortObj.sortType === "ASC" ? "transparent" : ""
                  }`}
                >
                  <img src={DownAngle} alt="Sorting" />
                </span>
              </span>
            </td>
            <td></td>
          </tr>
        </thead>
        <tbody>
          {allPeopleListLoading && props.list.length === 0 ? null : (
            <>
              {props.list.length > 0
                ? props.list.map((li: any, index: number) => {
                    return (
                      <UserListItem
                        contactId={li?.contact_id}
                        showDetailProfileHandler={showDetailProfileHandler}
                        imgUrl={li?.thumbnail}
                        name={li?.contact_name?.trim() || li?.contact_email}
                        color={li?.color}
                        designation={li?.designation}
                        organization={li?.organization}
                        industries={li?.industry}
                        personality={li?.personality}
                        engagement={li?.engagement_score}
                        new={false}
                        key={index}
                        listLength={props.list?.length}
                        isChecked={checkedItems.includes(li.contact_id)}
                        onClickChecked={() => onClickChecked(li?.contact_id, li?.contact_name)}
                        selectAll={selectAll}
                        tabState={tabState}
                        deleteAllFlag={deleteAllFlag}
                        contactCheckHandler={contactCheckHandler}
                        handleReselectItem={handleReselectItem}
                        status={li.status}
                        contactEmail={li?.contact_email}
                      />
                    );
                  })
                : null}
            </>
          )}
        </tbody>
      </table>
      <PeopleListHolder loading={allPeopleListLoading} />
      {allPeopleList.length === 0 &&
        !allPeopleListLoading &&
        searchTerm?.length === 0 &&
        !audienceFiltersApplied &&
        (getQueryParam("email_id") && getQueryParam("email_id").length > 0 ? (
          <div>
            <EmptyTabInfo
              description={
                <>
                  No workflow contacts are available. To view all contacts,{" "}
                  <span className="view-contacts" onClick={viewAllContactsClickHandler}>
                    click here
                  </span>
                </>
              }
            ></EmptyTabInfo>
          </div>
        ) : (
          <div>
            <EmptyTabInfo description="Your contacts will show up here once you will import a list or create new contact."></EmptyTabInfo>
          </div>
        ))}
      {(searchTerm?.length > 0 || audienceFiltersApplied) && !allPeopleListLoading && allPeopleList.length === 0 && (
        <>
          <EmptyTabInfo description="No contacts found against your search."></EmptyTabInfo>
        </>
      )}
      {props.sort ? (
        <div className="a-to-z">
          {/* Toggle active Class on active span */}
          <span className="top">A-Z</span>
          <br />
          <span>A</span>
          <br />
          <span>B</span>
          <br />
          <span>C</span>
          <br />
          <span>D</span>
          <br />
          <span>E</span>
          <br />
          <span>F</span>
          <br />
          <span>G</span>
          <br />
          <span>H</span>
          <br />
          <span>I</span>
          <br />
          <span>J</span>
          <br />
          <span>K</span>
          <br />
          <span>L</span>
          <br />
          <span>M</span>
          <br />
          <span>N</span>
          <br />
          <span>O</span>
          <br />
          <span>P</span>
          <br />
          <span>Q</span>
          <br />
          <span>R</span>
          <br />
          <span>S</span>
          <br />
          <span>T</span>
          <br />
          <span>U</span>
          <br />
          <span>V</span>
          <br />
          <span>W</span>
          <br />
          <span>X</span>
          <br />
          <span>Y</span>
          <br />
          <span>Z</span>
          <br />
        </div>
      ) : (
        ""
      )}
    </Wrapper>
  );
};

export default React.memo(UserList);

const Wrapper = styled.div<{ allPeopleListLoading: boolean }>`
  margin-bottom: 0px;
  position: relative;
  box-sizing: border-box;
  overflow-y: auto;
  height: calc(100vh - 310px);
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    height: calc(100vh - 252px);
  }
  .view-contacts {
    :hover {
      cursor: pointer;
    }
    color: #27ce0a;
  }
  .sortable-td {
    cursor: ${(props) => (props.allPeopleListLoading ? "auto" : "pointer")};
    pointer-events: ${(props) => (props.allPeopleListLoading ? "none" : "auto")};
  }
  .arrow-opacity {
    opacity: ${(props) => (props.allPeopleListLoading ? "0.5" : "1")};
  }
  .transparent {
    opacity: 0.5;
  }
  .engagement-float {
    float: revert !important;
  }
  .table-style {
    cursor: "pointer";
    height: 95px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 70px;
    }
  }
  td {
    span:first-child {
      &.left-align {
        float: left;
        margin-top: -7px;
      }
    }
  }
  .upArrow {
    width: 10px;
    height: 8px;
    margin-top: -16px;
    margin-left: 5px;
    float: left;
    cursor: pointer;
    @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: -14px;
    }
    img {
      transform: rotate(180deg);
    }
    &.engagement-arrow {
      float: right;
      margin-top: -9px;
      @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: -8px;
      }
    }
  }
  .downArrow {
    width: 10px;
    height: 8px;
    float: left;
    margin-left: 5px;
    margin-top: 8px;
    cursor: pointer;
    @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: 6px;
    }
    img {
      float: left;
    }
    &.engagement-arrow {
      float: right;
      margin-top: 14px;
      @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: 12px;
      }
      img {
        float: right;
      }
    }
  }
  &.set-table-height-people {
    height: calc(100vh - 405px);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: calc(100vh - 322px);
    }
  }
  scrollbar-width: thin;
  scrollbar-color: #001760 #efefef;
  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
    border-radius: 5px;
  }
  /* Track */
  &::-webkit-scrollbar-track {
    background: #fff;
    margin-top: 50px;
    margin-bottom: 0px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: 40px;
    }
  }
  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #001760;
  }
  &.sortable {
    min-height: 530px;
  }
  > div {
    overflow: hidden;
  }
  table {
    width: 100%;
    border: none;
    border-collapse: collapse;
    margin-bottom: 0px;
    thead {
      tr {
        &:before {
          max-height: 50px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            max-height: 38px;
          }
        }
      }
      td {
        color: #5e7079;
        font-size: 18px;
        font-family: "robotomedium";
        font-weight: 500;
        white-space: nowrap;
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #f9f8f6;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 14px;
        }
        &.h-name {
          padding-left: 75px;
          width: 472px;
          //Safari (7.1+)
          _::-webkit-full-page-media,
          _:future,
          :root .safari_only {
            width: 300px;
          }
          //Safari (from 6.1 to 10)
          @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
            @media {
              width: 300px;
            }
          }
          //Safari (10.1+)
          @media not all and (min-resolution: 0.001dpcm) {
            @media {
              width: 300px;
            }
          }
        }
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 400px;
        }
        &.company-col {
          width: 382px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 312px;
          }
        }
        &.personality-col {
          min-width: 127px;
        }
        &.status-col {
          min-width: 118px;
        }
        &.last-item {
          min-width: 190px;
          @media all and (max-width: 1690px) {
            min-width: 226px;
          }
        }
      }
    }
    .newly-added {
      background-color: rgba(39, 206, 10, 0.23);
      border-radius: 8px;
      border-radius: 8px;
      &:before {
        border: 3px solid #27ce0a;
        border-radius: 8px;
        height: calc(100% + 2px);
      }
      td {
        overflow: hidden;
        &:first-child {
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
        }
        &:last-child {
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
        }
      }
    }
    tr {
      border-radius: 8px;
      transition-duration: 0.2s;
      position: relative;
      &:before {
        content: "";
        border-bottom: 1px solid #dddddd;
        position: absolute;
        width: 100%;
        height: calc(100% + 3px);
        transition-duration: 0.2s;
        box-sizing: border-box;
        margin-top: -2px;
        height: 1px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          max-height: calc(100% + 2px);
        }
        @media all and (min-width: 1440px) {
          margin-top: -3px;
        }
      }
      &:first-child {
        &.active-new,
        &.newly-added {
          &:before {
            margin-top: -1px;
          }
        }
      }
      &.active-new {
        background: #cdf1d9;
        border-radius: 8px;
        border-bottom: none;
        &:before {
          border: 3px solid #27ce0a;
          border-radius: 8px;
          height: calc(100% + 2px);
          _::-webkit-full-page-media,
          _:future,
          :root .safari_only {
            height: 69px;
          }
          /* Safari (from 6.1 to 10)  */
          @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
            @media {
              /* Your Class */
              height: 69px;
            }
          }
          /* Safari (10.1+)  */
          @media not all and (min-resolution: 0.001dpcm) {
            @media {
              /* Your Class */
              height: 69px;
            }
          }
        }
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
      }
      &.active {
        background: #fff;
        border-radius: 8px;
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
      }
      &.disabled {
        pointer-events: none;
        opacity: 0.5;
      }
      &.drag-active {
        position: relative;
        border-radius: 8px;
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
        &:after {
          content: "";
          height: 100%;
          width: calc(100% + 10px);
          position: absolute;
          top: 0;
          left: 0;
          z-index: 99999;
          background-color: #f2efeb;
          border-radius: 8px;
          margin: 0 -5px;
          display: block;
        }
      }
      td {
        padding: 9px 10px;
        &.checkbox {
          width: 10px;
          padding-right: 0 !important;
        }
        &.last-item {
          text-align: right;
          padding-right: 45px;
        }
        &.name-col {
          width: 472px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 400px;
          }
        }
        .profile-img-wrapper {
          padding: 2px;
          width: 54px;
          height: 54px;
          border-radius: 100%;
          float: left;
          overflow: hidden;
          margin-top: 0px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 41px;
            height: 41px;
          }
          .profile-img {
            border-radius: 100%;
            width: 100%;
            height: 100%;
          }
        }
        .profile {
          float: left;
          padding-left: 14px;
          text-align: left;
          cursor: pointer;
          /* max-width: calc(100% - 50%); */
          .hidden-contact-name {
            height: 1px;
            overflow: hidden;
            display: block;
            opacity: 0;
            font-size: 20px;
            font-family: "Roboto Slab", serif;
          }
          width: 250px;
          max-width: calc(100% - 55px);
          //Safari (7.1+)
          _::-webkit-full-page-media,
          _:future,
          :root .safari_only {
            width: 250px;
          }
          //Safari (from 6.1 to 10)
          @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
            @media {
              width: 250px;
            }
          }
          //Safari (10.1+)
          @media not all and (min-resolution: 0.001dpcm) {
            @media {
              width: 250px;
            }
          }
          .name {
            color: #000000;
            font-size: 20px;
            font-family: "Roboto Slab", serif;
            margin-bottom: -5px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 14px;
            }
          }
          .email {
            color: #27ce0a;
            font-size: 14px;
            font-family: "robotomedium";
            font-weight: 500;
            margin-bottom: -3px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 10px;
            }
            &.designation {
              color: rgba(0, 0, 0, 0.8);
            }
          }
        }
        .organization {
          display: block;
          width: 100%;
          color: #424b50;
          font-size: 18px;
          font-family: "robotomedium";
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          text-overflow: ellipsis;
          max-width: 100%;
          &.company {
            width: 200px;
            max-width: calc(100% - 50px);
            overflow: hidden;
          }
          &.status-style-sub {
            color: #1cb84d;
            font-weight: bold;
          }
          &.status-style-unsub {
            color: #d50508;
            font-weight: bold;
          }
          &.status-style-bounce {
            color: #ff9531;
            font-weight: bold;
          }
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 13px;
          }
        }
        .industrie {
          display: block;
          width: 100%;
          font-size: 16px;
          font-family: "robotomedium";
          font-weight: 500;
          color: #5e7079;
          cursor: pointer;
          white-space: nowrap;
          text-overflow: ellipsis;
          max-width: 100%;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 12px;
          }
        }
        .engagement-v2 {
          border-radius: 12px;
          background-color: #eb7719;
          color: #ffffff;
          font-size: 16px;
          font-family: "robotomedium";
          font-weight: 500;
          padding: 0px 10px;
          display: inline;
          float: right;
          margin-right: 10px;
          cursor: pointer;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 12px;
          }
        }
        .new {
          border-radius: 12px;
          background-color: #27ce0a;
          color: #ffffff;
          font-size: 16px;
          font-family: "robotomedium";
          font-weight: 500;
          padding: 0px 10px;
          display: inline;
          float: right;
          margin-right: 10px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 12px;
          }
          svg path {
            fill: #fff;
          }
        }
        .engagement {
          border-radius: 12px;
          background-color: #1971eb;
          color: #ffffff;
          font-size: 16px;
          font-family: "robotomedium";
          font-weight: 500;
          padding: 0px 10px;
          margin-left: 35%;
          min-width: 80px;
          width: fit-content;
          display: inline;
          float: right;
          cursor: pointer;
          img {
            float: left;
            margin-top: 7px;
          }
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 12px;
            img {
              max-width: 13px;
              margin-top: 5px;
            }
          }
        }
        .float-right {
          float: right;
          margin-left: 15px;
        }
        &.last-item {
          span {
            float: right;
            margin-top: -7px;
          }
        }
      }
    }
  }
  .a-to-z {
    position: absolute;
    top: -75px;
    right: -55px;
    text-align: center;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-family: "robotomedium";
      font-weight: 500;
      color: #5e7079;
    }
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      right: -40px;
    }
    span {
      color: #5e7079;
      padding: 0px 8px;
      border-radius: 31px;
      margin: 2px 0;
      cursor: pointer;
      &.active,
      &.top {
        color: #ffffff;
        background-color: #424b50;
      }
    }
  }
`;
